import { TableCellTypes } from '@carboledger/ui';
import {
  makeLinkCell,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
} from '@/components/common';
import { ORDER_COLUMNS, ORDER_SORT_FIELDS } from './orders.constants';
import { buildStatusVariants } from './orderStatus';
import { ORDER_STATUS_TONE } from './orderStatusTone';
import { buildRowUnitOptions, unitTypeLabel } from './orderUnits';

// One entry per column; `sortable` is derived from ORDER_SORT_FIELDS below.
const canSort = (id) => Boolean(ORDER_SORT_FIELDS[id]);

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderContractedQuantity = makeUnitScopedCell('contractedQuantity');
const renderShippedQuantity = makeUnitScopedCell('shippedQuantity');
const renderRemainingToShip = makeUnitScopedCell('remainingToShipQuantity');
const renderAllocatedQuantity = makeUnitScopedCell('allocatedQuantity');
const renderRemainingToAllocate = makeUnitScopedCell('remainingToAllocateQuantity');

export const buildOrdersColumns = (t, { unitHeaderSelect, onOpenOrder, onOpenDiscrepancy }) =>
  [
    {
      id: ORDER_COLUMNS.orderNo,
      header: t('ORDERS_COL_ORDER_NO'),
      type: TableCellTypes.custom,
      width: 140,
      pinnable: true,
      cell: makeLinkCell(onOpenOrder),
    },
    {
      id: ORDER_COLUMNS.materialName,
      header: t('FIELD_MATERIAL_NAME'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.facilityName,
      header: t('FIELD_FACILITY_NAME'),
      type: TableCellTypes.text,
      width: 143,
    },
    {
      id: ORDER_COLUMNS.terminal,
      header: t('FIELD_TERMINAL'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: ORDER_COLUMNS.status,
      header: t('FIELD_STATUS'),
      type: TableCellTypes.statusDot,
      width: 110,
      variantMap: buildStatusVariants(t, ORDER_STATUS_TONE),
    },
    {
      id: ORDER_COLUMNS.soldTo,
      header: t('FIELD_SOLD_TO'),
      type: TableCellTypes.text,
      width: 192,
    },
    {
      id: ORDER_COLUMNS.shippingTo,
      header: t('FIELD_SHIPPING_TO'),
      type: TableCellTypes.text,
      width: 192,
    },
    {
      id: ORDER_COLUMNS.allocatedGhgSavings,
      header: t('ORDERS_COL_ALLOCATED_GHG_SAVINGS'),
      type: TableCellTypes.text,
      width: 192,
    },
    {
      id: ORDER_COLUMNS.unitType,
      header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
      type: TableCellTypes.custom,
      width: 252,
      cell: renderUnitTypeCell,
      headerSelect: unitHeaderSelect,
    },
    {
      id: ORDER_COLUMNS.contractedQuantity,
      header: t('FIELD_CONTRACTED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderContractedQuantity,
    },
    {
      id: ORDER_COLUMNS.shippedQuantity,
      header: t('FIELD_SHIPPED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderShippedQuantity,
    },
    {
      id: ORDER_COLUMNS.remainingToShipQuantity,
      header: t('ORDERS_COL_REMAINING_TO_SHIP'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderRemainingToShip,
    },
    {
      id: ORDER_COLUMNS.allocatedQuantity,
      header: t('FIELD_ALLOCATED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAllocatedQuantity,
    },
    {
      id: ORDER_COLUMNS.remainingToAllocateQuantity,
      header: t('ORDERS_COL_REMAINING_TO_ALLOCATE'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderRemainingToAllocate,
    },
    {
      id: ORDER_COLUMNS.tolerance,
      header: t('ORDERS_COL_TOLERANCE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.maxGhgEmissions,
      header: t('ORDERS_COL_MAX_GHG_EMISSIONS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.minGhgSavings,
      header: t('ORDERS_COL_MIN_GHG_SAVINGS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.rawMaterialType,
      header: t('FIELD_RAW_MATERIAL_TYPE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.countryOfOrigin,
      header: t('FIELD_COUNTRY_OF_ORIGIN'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.feedstockCategory,
      header: t('FIELD_FEEDSTOCK_CATEGORY'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.allocationPreference,
      header: t('FIELD_ALLOCATION_PREFERENCE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.dispatchBy,
      header: t('FIELD_DISPATCH_BY'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: ORDER_COLUMNS.discrepancies,
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ].map((column) => ({ ...column, sortable: canSort(column.id) }));
